import { noop } from 'lodash';
import { useCallback, useId, useRef, useState } from 'react';
import * as ActionTypes from '../../../components/SearchForm/actionTypes.js';
import { useAirportsField } from '../../../components/SearchForm/components/AirportsField/hooks/useAirportsField.js';
import { useDestinationField } from '../../../components/SearchForm/components/DestinationField/index.js';
import { useStorage } from '../../../components/SearchForm/components/DestinationWidget/components/BlockLastSearches/hooks/useStorage.js';
import { useTravellersField } from '../../../components/SearchForm/components/TravellersField/index.js';
import { useTravellersFieldMultiroom } from '../../../components/SearchForm/components/TravellersFieldMultiroom/index.js';
import { useTravelPeriodField } from '../../../components/SearchForm/components/TravelPeriodField/index.js';
import { useTravelPeriodFieldNew } from '../../../components/SearchForm/components/TravelPeriodField/hooks/useTravelPeriodFieldNew.js';
import { useTravelTypeField } from '../../../components/SearchForm/components/TravelTypeField/index.js';
import { FIELD_AIRPORTS, FIELD_DESTINATION, FIELD_TRAVEL_PERIOD, FIELD_TRAVEL_TYPE, FIELD_TRAVELLERS, FIELD_TRAVELLERS_MULTIROOM } from '../../../components/SearchForm/models.js';
import { isSsr } from '../../../utils/index.js';
export const useSearchForm = ({
  travelType,
  values: controlledValues,
  prevValues,
  defaultValues,
  config,
  dispatch,
  onSubmit = noop,
  onClickLastSearchesItem = noop
}) => {
  // "id"-attribute added to the form element so we can target it for positioning
  const id = useId();
  const idRef = useRef(id);
  // positioning (floating-ui) options shared by all widgets
  const floatingOptions = {
    shiftBoundaryElementId: isSsr ? undefined : idRef.current,
    scrollToTopOnClose: config.scrollToTopOnWidgetClose
  };
  const {
    layout,
    hasNextDP,
    dimming,
    airportMapping,
    multiroom: multiroomEnabled
  } = config;
  const isCompactLayout = layout === 'compact' || layout === 'sidebar';
  const popOverBottomStart = isCompactLayout ? 'bottom-start' : 'bottom';
  const {
    add: addStorageItem,
    data
  } = useStorage();
  const lastSearches = data.length > 0 && config.destinationField.recentSearches;
  const values = {
    [FIELD_DESTINATION]: controlledValues.destination ?? defaultValues?.destination,
    [FIELD_AIRPORTS]: controlledValues.airports ?? defaultValues?.airports,
    [FIELD_TRAVELLERS]: controlledValues.travellers ?? defaultValues?.travellers,
    [FIELD_TRAVELLERS_MULTIROOM]: controlledValues.travellersMultiroom ?? defaultValues?.travellersMultiroom ?? {
      adultsCount: 0,
      childrenCount: 0,
      rooms: []
    },
    [FIELD_TRAVEL_PERIOD]: controlledValues.travelPeriod ?? defaultValues?.travelPeriod
  };
  // dirty: there is an ongoing entry in the field
  // invalid: there is an ongoing entry in the field and the value has not passed validation
  // touched: the has been a manual entry in the field (as opposed to a programmatic change,
  //  e.g. via event dispatch) and the value differs from the default one
  const [fieldState, setFieldState] = useState({
    [FIELD_DESTINATION]: {
      dirty: false,
      invalid: false
    },
    [FIELD_TRAVELLERS]: {
      dirty: false,
      invalid: false
    },
    [FIELD_TRAVELLERS_MULTIROOM]: {
      dirty: false,
      invalid: false
    },
    [FIELD_AIRPORTS]: {
      dirty: false,
      invalid: false,
      touched: false
    },
    [FIELD_TRAVEL_PERIOD]: {
      dirty: false,
      invalid: false
    }
  });
  const updateFieldState = (field, state) => {
    setFieldState(previous => ({
      ...previous,
      // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      [field]: {
        ...previous[field],
        ...state
      }
    }));
  };
  const someFieldDirty = Object.values(fieldState).some(field => field.dirty);
  const dispatchAirportsField = useCallback(event => {
    if (event.type === ActionTypes.airportsField.AirportToggled) {
      updateFieldState(FIELD_AIRPORTS, {
        touched: true
      });
    }
    if (event.type === ActionTypes.airportsField.ResetClicked || event.type === ActionTypes.airportsField.FunctionValueChanged) {
      updateFieldState(FIELD_AIRPORTS, {
        touched: false
      });
    }
    dispatch(event);
  }, [dispatch]);
  const dispatchDestinationField = useCallback(event => {
    // Mark field as invalid on reset
    if (event.type === ActionTypes.destinationField.ResetClicked) {
      updateFieldState(FIELD_DESTINATION, {
        invalid: true
      });
    }
    if (event.type === ActionTypes.destinationField.ValueChanged || event.type === ActionTypes.destinationField.FunctionValueChanged || event.type === ActionTypes.destinationField.InputChanged) {
      updateFieldState(FIELD_DESTINATION, {
        // Mark field as invalid on reset
        invalid: false
      });
    }
    if ((event.type === ActionTypes.destinationField.ValueChanged || event.type === ActionTypes.destinationField.FunctionValueChanged) && airportMapping && !fieldState[FIELD_AIRPORTS].touched) {
      const airports = airportMapping.find(({
        destinations
      }) =>
      // TODO: This is a placeholder for the multi-destination matching
      destinations.includes(event.payload?.value.id))?.airports;
      if (airports) {
        setValue(FIELD_AIRPORTS, airports);
      }
    }
    dispatch(event);
  }, [dispatch]);
  const fields = {
    [FIELD_TRAVEL_TYPE]: useTravelTypeField({
      value: travelType,
      config: config.travelTypeField,
      dispatch
    }),
    [FIELD_DESTINATION]: useDestinationField({
      value: values.destination,
      config: {
        ...config.destinationField,
        airportsField: config.airportsField,
        travelType: travelType
      },
      floatingOptions: {
        ...floatingOptions,
        placement: 'bottom-start'
      },
      invalid: fieldState.destination.invalid,
      disabled: someFieldDirty && !fieldState.destination.dirty,
      dirty: fieldState.destination.dirty,
      layout,
      dimming,
      dispatch: dispatchDestinationField,
      onClickLastSearchesItem: async values => {
        setValues(values.values);
        dispatch({
          type: ActionTypes.FormSubmitted,
          payload: {
            travelType,
            values: values.values,
            prevValues,
            invalid: false,
            multiroomEnabled,
            lastSearches,
            clickLastSearches: true
          }
        });
        onClickLastSearchesItem(values);
      }
    }),
    [FIELD_AIRPORTS]: useAirportsField({
      value: values.airports,
      config: config.airportsField,
      floatingOptions: {
        ...floatingOptions
      },
      invalid: fieldState.airports.invalid,
      disabled: someFieldDirty && !fieldState.airports.dirty,
      dirty: fieldState.airports.dirty,
      layout,
      dimming,
      dispatch: dispatchAirportsField
    })
  };
  const travelPeriodField = useTravelPeriodField({
    value: values[FIELD_TRAVEL_PERIOD],
    config: config.travelPeriodField,
    floatingOptions: {
      ...floatingOptions,
      placement: popOverBottomStart
    },
    invalid: fieldState[FIELD_TRAVEL_PERIOD].invalid,
    disabled: someFieldDirty && !fieldState[FIELD_TRAVEL_PERIOD].dirty,
    dirty: fieldState[FIELD_TRAVEL_PERIOD].dirty,
    layout,
    dimming,
    dispatch
  });
  const travelPeriodFieldNew = useTravelPeriodFieldNew({
    value: values[FIELD_TRAVEL_PERIOD],
    config: config.travelPeriodField,
    floatingOptions: {
      ...floatingOptions,
      placement: popOverBottomStart
    },
    invalid: fieldState[FIELD_TRAVEL_PERIOD].invalid,
    disabled: someFieldDirty && !fieldState[FIELD_TRAVEL_PERIOD].dirty,
    dirty: fieldState[FIELD_TRAVEL_PERIOD].dirty,
    layout,
    dimming,
    dispatch
  });
  const travellersField = useTravellersField({
    value: values[FIELD_TRAVELLERS],
    defaultValue: defaultValues?.[FIELD_TRAVELLERS],
    config: config.travellersField,
    floatingOptions: {
      ...floatingOptions,
      placement: popOverBottomStart
    },
    invalid: fieldState[FIELD_TRAVELLERS].invalid,
    disabled: someFieldDirty && !fieldState[FIELD_TRAVELLERS].dirty,
    dirty: fieldState[FIELD_TRAVELLERS].dirty,
    dimming,
    layout,
    dispatch: event => {
      const invalid = !event.payload.valid;
      if (event.type === ActionTypes.travellersField.ValueChanged && invalid) {
        event.preventDefault();
        updateFieldState(FIELD_TRAVELLERS, {
          invalid: true
        });
        return;
      }
      updateFieldState(FIELD_TRAVELLERS, {
        dirty: invalid,
        invalid: fieldState[FIELD_TRAVELLERS].invalid // only update when already invalid
        ? invalid : fieldState[FIELD_TRAVELLERS].invalid
      });
      dispatch(event);
    }
  });
  const travellersFieldMultiroom = useTravellersFieldMultiroom({
    value: values[FIELD_TRAVELLERS_MULTIROOM],
    defaultValue: defaultValues?.[FIELD_TRAVELLERS_MULTIROOM] ?? undefined,
    config: config.travellersFieldMultiroom,
    dimming,
    floatingOptions: {
      ...floatingOptions,
      placement: popOverBottomStart
    },
    invalid: fieldState[FIELD_TRAVELLERS_MULTIROOM].invalid,
    disabled: someFieldDirty && !fieldState[FIELD_TRAVELLERS_MULTIROOM].dirty,
    dirty: fieldState[FIELD_TRAVELLERS_MULTIROOM].dirty,
    dispatch: event => {
      const invalid = !event.payload.valid;
      if (event.type === ActionTypes.travellersFieldMultiroom.ValueChanged && invalid) {
        event.preventDefault();
        updateFieldState(FIELD_TRAVELLERS_MULTIROOM, {
          invalid: true
        });
        return;
      }
      updateFieldState(FIELD_TRAVELLERS_MULTIROOM, {
        dirty: invalid,
        invalid: fieldState[FIELD_TRAVELLERS_MULTIROOM].invalid // only update when already invalid
        ? invalid : fieldState[FIELD_TRAVELLERS_MULTIROOM].invalid
      });
      dispatch(event);
    }
  });
  fields[FIELD_TRAVEL_PERIOD] = config.hasNextDP ? travelPeriodFieldNew : travelPeriodField;
  if (multiroomEnabled) {
    fields[FIELD_TRAVELLERS_MULTIROOM] = travellersFieldMultiroom;
  } else {
    fields[FIELD_TRAVELLERS] = travellersField;
  }
  const handleSubmit = () => {
    let invalid = false;
    for (const fieldName in fields) {
      if (fieldName === FIELD_TRAVEL_TYPE) {
        continue;
      }
      if (!fields[fieldName].checkValidity()) {
        invalid = true;
        updateFieldState(fieldName, {
          invalid: true
        });
        break;
      }
    }
    dispatch({
      type: ActionTypes.FormSubmitted,
      payload: {
        travelType,
        values,
        prevValues,
        invalid,
        multiroomEnabled,
        lastSearches
      }
    });
    addStorageItem({
      travelType,
      values,
      invalid
    });
    onSubmit({
      travelType,
      values,
      invalid
    });
  };
  const register = () => ({
    id: idRef.current,
    travelType,
    fields,
    submitButtonVisible: config.submitButtonVisible,
    submitButtonLabel: config.submitButtonLabel,
    layout,
    dimming,
    hasNextDP,
    onSubmit: handleSubmit
  });
  const setValue = (name, value) => {
    fields[name]?.setValue(value);
  };
  const setValues = values => {
    for (const name in values) {
      // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      setValue(name, values[name]);
    }
  };
  return {
    register,
    setValue,
    setValues
  };
};